import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  isStandalonePWA,
  getCurrentPushSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/lib/pushClient";

/**
 * PushNotificationCard — coach + student facing panel for enabling web push.
 *
 * Shows the current opt-in state, an Enable/Disable button, and a
 * "Send test" affordance so the user can verify the pipeline end-to-end
 * before an actual message/booking event arrives.
 *
 * Handles the common gotchas:
 *   • Unsupported browser (older Safari, non-installed iOS)
 *   • Permission previously denied — points to browser settings
 *   • Already subscribed — surface the last-modified timestamp
 */
export default function PushNotificationCard() {
  const [supported, setSupported] = useState(true);
  const [installed, setInstalled] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSupported(isPushSupported());
    setInstalled(isStandalonePWA());
    (async () => {
      const s = await getCurrentPushSubscription();
      setSubscribed(!!s);
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    const res = await subscribeToPush();
    setBusy(false);
    if (res.ok) {
      setSubscribed(true);
      setPermission("granted");
      toast.success("Push notifications enabled");
    } else {
      toast.error(res.reason || "Couldn't enable notifications");
    }
  };
  const disable = async () => {
    setBusy(true);
    await unsubscribeFromPush();
    setBusy(false);
    setSubscribed(false);
    toast.success("Push notifications disabled");
  };
  const testPush = async () => {
    setBusy(true);
    try {
      const d = await sendTestPush();
      if (d.delivered_to_devices > 0) {
        toast.success(`Test sent to ${d.delivered_to_devices} device${d.delivered_to_devices === 1 ? "" : "s"} — check your notification tray.`);
      } else {
        toast.error("No devices to deliver to — enable notifications first.");
      }
    } catch (e) {
      toast.error("Test send failed — try disabling + re-enabling.");
    }
    setBusy(false);
  };

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="card-surface p-6" data-testid="push-notification-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold flex items-center gap-1.5">
            <Bell size={11} /> Push notifications
          </div>
          <h3 className="font-display text-2xl font-black uppercase tracking-tight mt-1">
            Stay in the loop
          </h3>
          <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed max-w-lg">
            Get notified on your device when you receive new messages, bookings, capture analyses, or lesson reminders. Works even when the app is closed.
          </p>
        </div>
        <div>
          {subscribed ? (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#00ff88]/40 text-[#00ff88] px-2 py-1"
              data-testid="push-state-on"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88]" /> ON
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold border border-white/15 text-[#A3A3A3] px-2 py-1"
              data-testid="push-state-off"
            >
              OFF
            </span>
          )}
        </div>
      </div>

      {!supported && (
        <div className="mt-4 text-[11px] text-[#A3A3A3] border-l-2 border-white/15 pl-3">
          This browser doesn&apos;t support push notifications. Try Chrome, Edge, Firefox, or Safari 16.4+.
        </div>
      )}

      {supported && isIos && !installed && (
        <div className="mt-4 text-[11px] text-[#ffab00] border-l-2 border-[#ffab00]/60 pl-3 leading-relaxed" data-testid="push-ios-hint">
          iOS requires installing this app to your Home Screen first. Tap the browser&apos;s <strong>Share</strong> icon → <strong>Add to Home Screen</strong>, then reopen from your Home Screen and come back here to enable.
        </div>
      )}

      {supported && permission === "denied" && (
        <div className="mt-4 text-[11px] text-red-400 border-l-2 border-red-400/60 pl-3 leading-relaxed" data-testid="push-denied-hint">
          Notifications are blocked in your browser settings. Open the site settings for pbcoach.app and re-allow notifications, then reload.
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {!subscribed && (
          <button
            onClick={enable}
            disabled={busy || (isIos && !installed)}
            className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="push-enable-btn"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
            Enable notifications
          </button>
        )}
        {subscribed && (
          <>
            <button
              onClick={testPush}
              disabled={busy}
              className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="push-test-btn"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
              Send test
            </button>
            <button
              onClick={disable}
              disabled={busy}
              className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="push-disable-btn"
            >
              <BellOff size={13} />
              Disable
            </button>
          </>
        )}
      </div>
    </div>
  );
}
